import { CONTEUDO_CHAVES, getConteudoChave } from '@/api/adminApi'
import { isConteudoFlagEnabled, isConteudoFlagInData } from '@/utils/featureFlags'

export type AgendamentoFlags = {
  /** Botão «Agendar» na barra inferior e página /agendamento */
  fluxo: boolean
  /** Botão «Histórico» na barra e página /historico */
  historico: boolean
  /** Cartões em Serviços abrem a marcação */
  servicosMarcacao: boolean
}

function resolveTriplet(
  fluxoRaw: string | null,
  historicoRaw: string | null,
  servicosRaw: string | null,
  legacyRaw: string | null
): AgendamentoFlags {
  const hasNew =
    (fluxoRaw != null && fluxoRaw !== '') ||
    (historicoRaw != null && historicoRaw !== '') ||
    (servicosRaw != null && servicosRaw !== '')

  if (!hasNew) {
    const legacy = isConteudoFlagEnabled(legacyRaw)
    return { fluxo: legacy, historico: legacy, servicosMarcacao: legacy }
  }

  return {
    fluxo: isConteudoFlagEnabled(fluxoRaw ?? legacyRaw),
    historico: isConteudoFlagEnabled(historicoRaw ?? legacyRaw),
    servicosMarcacao: isConteudoFlagEnabled(servicosRaw ?? legacyRaw),
  }
}

/** Página /agendamento acessível se o fluxo ou a marcação em serviços estiver ativa. */
export function canAccessAgendamentoPage(flags: AgendamentoFlags): boolean {
  return flags.fluxo || flags.servicosMarcacao
}

export async function fetchAgendamentoFlags(): Promise<AgendamentoFlags> {
  const [fluxoRaw, historicoRaw, servicosRaw, legacyRaw] = await Promise.all([
    getConteudoChave(CONTEUDO_CHAVES.agendamento_fluxo_enabled).catch(() => null),
    getConteudoChave(CONTEUDO_CHAVES.historico_cliente_enabled).catch(() => null),
    getConteudoChave(CONTEUDO_CHAVES.servicos_marcacao_enabled).catch(() => null),
    getConteudoChave(CONTEUDO_CHAVES.agendamentos_enabled).catch(() => null),
  ])
  return resolveTriplet(fluxoRaw, historicoRaw, servicosRaw, legacyRaw)
}

export function resolveAgendamentoFlagsFromRaw(
  fluxoRaw: string | null,
  historicoRaw: string | null,
  servicosRaw: string | null,
  legacyRaw: string | null
): AgendamentoFlags {
  return resolveTriplet(fluxoRaw, historicoRaw, servicosRaw, legacyRaw)
}

/** Resolve flags a partir do mapa de conteúdo (sem assumir ativo se a chave não existir). */
export function resolveAgendamentoFlagsFromData(
  data: Record<string, string | null>
): AgendamentoFlags {
  const fluxoKey = CONTEUDO_CHAVES.agendamento_fluxo_enabled
  const historicoKey = CONTEUDO_CHAVES.historico_cliente_enabled
  const servicosKey = CONTEUDO_CHAVES.servicos_marcacao_enabled
  const legacyKey = CONTEUDO_CHAVES.agendamentos_enabled

  const hasNew = fluxoKey in data || historicoKey in data || servicosKey in data
  if (!hasNew) {
    if (!(legacyKey in data)) {
      return { fluxo: false, historico: false, servicosMarcacao: false }
    }
    const legacy = isConteudoFlagEnabled(data[legacyKey])
    return { fluxo: legacy, historico: legacy, servicosMarcacao: legacy }
  }

  const legacyVal = legacyKey in data ? isConteudoFlagEnabled(data[legacyKey]) : false
  return {
    fluxo:
      fluxoKey in data
        ? isConteudoFlagInData(data, fluxoKey)
        : legacyVal,
    historico:
      historicoKey in data
        ? isConteudoFlagInData(data, historicoKey)
        : legacyVal,
    servicosMarcacao:
      servicosKey in data
        ? isConteudoFlagInData(data, servicosKey)
        : legacyVal,
  }
}
